import React, { lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Loadable from '@ui/components/Loadable';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useTracking } from '@ui/hooks/useTracking';

const Register = Loadable(lazy(() => import('./pages/account/Register')));
const Login = Loadable(lazy(() => import('./pages/account/Login')));
const PasswordRecover = Loadable(lazy(() => import('./pages/account/PasswordRecover')));
const PasswordRecoverConfirm = Loadable(
  lazy(() => import('./pages/account/PasswordRecoverConfirm'))
);
const RegisterConfirm = Loadable(lazy(() => import('./pages/account/RegisterConfirm')));
const MoreRegisterConfirmOptions = Loadable(
  lazy(() => import('./pages/account/MoreRegisterConfirmOptions'))
);
const RegisterChangeEmailConfirmOptions = Loadable(
  lazy(() => import('./pages/account/RegisterChangeEmailConfirmOptions'))
);
const RegisterChangePhoneConfirmOptions = Loadable(
  lazy(() => import('./pages/account/RegisterChangePhoneConfirmOptions'))
);

const Home = Loadable(lazy(() => import('./pages/home/Home')));
const Referrals = Loadable(lazy(() => import('./pages/referrals/Referrals')));
const AddReferral = Loadable(lazy(() => import('./pages/referrals/AddReferral')));
const ReferralsWithdrawal = Loadable(lazy(() => import('./pages/referrals/ReferralsWithdrawal')));
const Notifications = Loadable(lazy(() => import('./pages/notifications/Notifications')));
const ProfileSetupRegisterIntroFlow = Loadable(
  lazy(() => import('./pages/profile/RegisterFlows/OnboardingFlow'))
);
const Settings = Loadable(lazy(() => import('./pages/settings/Settings')));
const SettingsRemove = Loadable(lazy(() => import('./pages/settings/RemoveAccount')));
const SettingsPersonalInformation = Loadable(
  lazy(() => import('./pages/settings/PersonalInformation'))
);
const SettingsCompanyInformation = Loadable(
  lazy(() => import('./pages/settings/CompanyInformation'))
);
const SettingsHelp = Loadable(lazy(() => import('./pages/settings/Help')));
const ReferPartner = Loadable(lazy(() => import('./pages/settings/ReferPartner')));
const SettingsUsers = Loadable(lazy(() => import('./pages/settings/Users')));
const SettingsAddUser = Loadable(lazy(() => import('./pages/settings/AddUser')));
const SettingsBankDetails = Loadable(lazy(() => import('./pages/settings/BankDetails')));
const SettingsBankDetailsSubmit = Loadable(
  lazy(() => import('./pages/settings/BankDetailsSubmit'))
);

function RoutesComponent() {
  const location = useLocation();
  const { trackPage } = useTracking();

  useEffect(() => {
    trackPage({
      data: location?.pathname
    });
  }, [location, trackPage]);

  return (
    <Routes>
      <Route path="/register" element={<Register />} />
      <Route path="/register/:email" element={<Register />} />
      <Route path="/register/confirm" element={<RegisterConfirm />} />
      <Route path="/register/confirm/:email" element={<RegisterConfirm />} />
      <Route path="/register/confirm/:email/:code" element={<RegisterConfirm />} />
      <Route
        path="/register/confirm/more-options/:email"
        element={<MoreRegisterConfirmOptions />}
      />
      <Route
        path="/register/confirm/more-options/:email/email"
        element={<RegisterChangeEmailConfirmOptions />}
      />
      <Route
        path="/register/confirm/more-options/:email/phone"
        element={<RegisterChangePhoneConfirmOptions />}
      />
      <Route path="/login" element={<Login />} />
      <Route path="/login/:email" element={<Login />} />
      <Route path="/password/recover" element={<PasswordRecover />} />
      <Route path="/password/recover/:email" element={<PasswordRecover />} />
      <Route path="/password/recover/confirm/:email" element={<PasswordRecoverConfirm />} />
      <Route path="/password/recover/confirm/:email/:code" element={<PasswordRecoverConfirm />} />

      <Route
        path="/"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Home />
          </ProtectedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/intro"
        element={
          <ProtectedRoute>
            <ProfileSetupRegisterIntroFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Referrals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals/add"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <AddReferral />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals/withdrawal"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <ReferralsWithdrawal />
          </ProtectedRoute>
        }
      />
      <Route path="/r/:refId" element={<Register />} />
      <Route path="/r/:refId/:email" element={<Register />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/remove"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsRemove />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/personal-information"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsPersonalInformation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/bank-details"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsBankDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/bank-details/submit"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsBankDetailsSubmit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/company-information"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsCompanyInformation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/partner/refer"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <ReferPartner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/help"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsHelp />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users/add"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsAddUser />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default RoutesComponent;
